import { orderBy } from "es-toolkit/compat";
import { useMemo } from "react";

import type { BoundingBox, Point } from "../types.ts";
import { getCurvableConnector } from "./curvableConnector.tsx";
import { getDrawingCurveDescriptor } from "./curvableDrawing.ts";
import { defaultCurveConfig } from "./curve.ts";
import { getGeometricScene } from "./geometricScene.ts";
import type { CurvableDrawing, DrawingState, Edge, GeometricDrawing, SceneBoundsMap } from "./types.ts";
import type { MousePosition } from "./useMousePosition.ts";
import { getOptimalEdges } from "./useOptimalEdges.ts";

const edges: Edge[] = ["TOP", "BOTTOM", "LEFT", "RIGHT"];

const getSceneAnchorPoint = (sceneBounds: BoundingBox, edge: Edge): Point => {
  switch (edge) {
    case "TOP":
      return {
        x: sceneBounds.x + sceneBounds.width / 2,
        y: sceneBounds.y,
      };

    case "BOTTOM":
      return {
        x: sceneBounds.x + sceneBounds.width / 2,
        y: sceneBounds.y + sceneBounds.height,
      };

    case "LEFT":
      return {
        x: sceneBounds.x,
        y: sceneBounds.y + sceneBounds.height / 2,
      };

    case "RIGHT":
      return {
        x: sceneBounds.x + sceneBounds.width,
        y: sceneBounds.y + sceneBounds.height / 2,
      };
  }
};

const getOptimalEdge = (sceneBounds: BoundingBox, mousePosition: MousePosition): Edge => {
  const augmentedEdges = edges.map((edge) => {
    const point = getSceneAnchorPoint(sceneBounds, edge);
    return {
      edge,
      distance: Math.hypot(mousePosition.x - point.x, mousePosition.y - point.y),
    };
  });
  return orderBy(augmentedEdges, "distance")[0].edge;
};

export const useDrawing = (
  { startingSceneId, endingSceneId }: DrawingState,
  sceneBounds: SceneBoundsMap,
  mousePosition?: MousePosition,
): CurvableDrawing => {
  // Derive the connector when we have both start and end scenes
  const connector = useMemo(() => {
    if (!endingSceneId || endingSceneId === startingSceneId) {
      return undefined;
    }

    const optimalEdges = getOptimalEdges(
      getGeometricScene(sceneBounds[startingSceneId]),
      getGeometricScene(sceneBounds[endingSceneId]),
      defaultCurveConfig,
    );

    return getCurvableConnector({
      connector: {
        id: "drawing",
        from: {
          id: startingSceneId,
          bounds: sceneBounds[startingSceneId],
        },
        to: {
          id: endingSceneId,
          bounds: sceneBounds[endingSceneId],
        },
        bidirectional: false,
        state: "active",
      },
      optimalEdges,
      options: defaultCurveConfig,
    });
  }, [startingSceneId, endingSceneId, sceneBounds]);

  // Derive the starting scene edge based on optimal edges or mouse position
  const startingSceneEdge = useMemo(() => {
    // If we have a connector, use its optimal edge
    if (connector) {
      const optimalEdges = getOptimalEdges(
        getGeometricScene(sceneBounds[startingSceneId]),
        getGeometricScene(sceneBounds[endingSceneId!]),
        defaultCurveConfig,
      );
      return optimalEdges.from;
    }

    // If we're drawing to mouse position, find optimal edge for mouse
    if (mousePosition && !endingSceneId) {
      return getOptimalEdge(sceneBounds[startingSceneId], mousePosition);
    }

    return undefined;
  }, [connector, mousePosition, endingSceneId, sceneBounds, startingSceneId]);

  if (connector) return { connector };
  if (!startingSceneEdge || !mousePosition) return {};

  const startingPoint = getSceneAnchorPoint(sceneBounds[startingSceneId], startingSceneEdge);

  const geometricDrawing: GeometricDrawing = {
    from: { point: startingPoint, edge: startingSceneEdge },
    to: mousePosition,
  };

  const curveDescriptor = getDrawingCurveDescriptor({
    drawing: geometricDrawing,
    options: defaultCurveConfig,
  });

  return { curveDescriptor };
};
